'use client';

import {
  APP_SHELL_FOOTER_CLEARANCE,
  APP_SHELL_MAIN_PORTAL_ID,
} from '@/components/layout/dashboard/dashboard-shell.layout';
import { WithClassName } from '@/types/component';
import { cn } from '@/utils/shad';
import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type BottomCtaPortalProps = React.PropsWithChildren &
  WithClassName &
  React.HTMLAttributes<HTMLDivElement> & {
    /** `fixed` pins to the viewport bottom; `inline` renders in place above surrounding content. */
    placement?: 'fixed' | 'inline';
  };

const bottomCtaClassName =
  'flex items-center rounded-base border bg-popover px-3 py-1 shadow-md';

export const BottomCtaPortal = ({
  children,
  className,
  placement = 'fixed',
  ...rest
}: BottomCtaPortalProps) => {
  const [portalState, setPortalState] = useState<{
    target: HTMLElement;
    isMainContent: boolean;
  } | null>(null);

  useLayoutEffect(() => {
    const mainContent = document.getElementById(APP_SHELL_MAIN_PORTAL_ID);
    setPortalState({
      target: mainContent ?? document.body,
      isMainContent: mainContent != null,
    });
  }, []);

  if (placement === 'inline') {
    return (
      <div className={cn(bottomCtaClassName, className)} {...rest}>
        {children}
      </div>
    );
  }

  if (!portalState) return null;

  return createPortal(
    <div
      className={cn(
        portalState.isMainContent ? 'absolute' : 'fixed',
        'left-1/2 z-50 -translate-x-1/2',
        bottomCtaClassName,
        className,
      )}
      style={{ bottom: APP_SHELL_FOOTER_CLEARANCE }}
      {...rest}
    >
      {children}
    </div>,
    portalState.target,
  );
};
